import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import {
  collection,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useFirestore } from "reactfire";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import MainLayout from "../../layouts/main_layout/main_layout";
import { buildNewsPath, buildNewsSlug, extractNewsId } from "../../utils/news";
import "./styles.css";

export default function News() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [recordDetails, setRecordDetails] = useState<DocumentData | null>(null);
  const [resolvedNewsId, setResolvedNewsId] = useState("");
  const firestore = useFirestore();

  useEffect(() => {
    const getSpecificRecord = async () => {
      if (!id) {
        return;
      }

      setIsLoading(true);
      try {
        const newsId = extractNewsId(id);
        let docSnap = await getDoc(doc(firestore, "news", newsId));

        if (docSnap.exists()) {
          setRecordDetails(docSnap.data());
          setResolvedNewsId(docSnap.id);
        } else {
          const slugQuery = query(
            collection(firestore, "news"),
            where("slug", "==", id),
            limit(1),
          );
          const slugSnapshot = await getDocs(slugQuery);
          const slugDoc = slugSnapshot.docs[0];

          if (slugDoc) {
            setRecordDetails(slugDoc.data());
            setResolvedNewsId(slugDoc.id);
          } else {
            setRecordDetails(null);
            setResolvedNewsId("");
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    getSpecificRecord();
  }, [firestore, id]);

  const canonicalPath = useMemo(() => {
    return buildNewsPath(
      recordDetails?.title,
      resolvedNewsId,
      recordDetails?.slug ||
        buildNewsSlug(recordDetails?.title, resolvedNewsId),
    );
  }, [recordDetails?.slug, recordDetails?.title, resolvedNewsId]);
  const canonicalUrl = `https://youthcommissionudupi.in${canonicalPath}`;
  const socialImage = useMemo(
    () => recordDetails?.thumbnail || recordDetails?.gallery?.[0] || "",
    [recordDetails],
  );
  const hasGallery = Boolean(recordDetails?.gallery?.length);

  useEffect(() => {
    if (!recordDetails || !resolvedNewsId) {
      return;
    }

    if (location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [
    canonicalPath,
    location.pathname,
    navigate,
    recordDetails,
    resolvedNewsId,
  ]);

  return (
    <div>
      <Helmet>
        <title>{recordDetails?.title || "News"}</title>
        <meta
          name="description"
          content={recordDetails?.subtitle || "Latest updates from ICYM Udupi."}
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={recordDetails?.title || "News"} />
        <meta
          property="og:description"
          content={recordDetails?.subtitle || "Latest updates from ICYM Udupi."}
        />
        <meta property="og:url" content={canonicalUrl} />
        {socialImage ? (
          <meta property="og:image" content={socialImage} />
        ) : null}
        {socialImage ? <meta name="image" content={socialImage} /> : null}
        <meta
          name="twitter:card"
          content={socialImage ? "summary_large_image" : "summary"}
        />
        <meta name="twitter:title" content={recordDetails?.title || "News"} />
        <meta
          name="twitter:description"
          content={recordDetails?.subtitle || "Latest updates from ICYM Udupi."}
        />
        {socialImage ? (
          <meta name="twitter:image" content={socialImage} />
        ) : null}
        <meta name="url" content={canonicalUrl} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <MainLayout>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100vh"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid
            container
            justifyContent="center"
            spacing={{ xs: 2, md: 3 }}
            sx={{ px: { xs: 0.5, sm: 1, md: 0 } }}
          >
            <Grid
              item
              xs={12}
              sm={12}
              md={7}
              lg={7}
              display="flex"
              justifyContent="flex-end"
              flexDirection="column"
              sx={{ minWidth: 0 }}
            >
              <Box
                display="flex"
                justifyContent="center"
                mt={{ xs: 2, md: 4 }}
                width="100%"
              >
                <Typography
                  display="flex"
                  textAlign="center"
                  sx={{
                    fontSize: {
                      xs: 24,
                      md: 26,
                    },
                    fontWeight: 700,
                    lineHeight: 1.25,
                    overflowWrap: "anywhere",
                  }}
                >
                  {recordDetails?.title}
                </Typography>
              </Box>
              <Box mt={{ xs: 1.5, md: 2 }}>
                <Typography
                  fontWeight={600}
                  sx={{
                    fontSize: {
                      xs: 16,
                      md: 22,
                    },
                    textAlign: { xs: "center", md: "left" },
                    overflowWrap: "anywhere",
                  }}
                >
                  {recordDetails?.subtitle}
                </Typography>
              </Box>
              <Box mt={{ xs: 3, md: 4 }}>
                <Box
                  display="flex"
                  mb={2}
                  flexDirection={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  gap={{ xs: 1.5, sm: 0 }}
                >
                  <Box
                    width="fit-content"
                    border="1px solid black"
                    borderRadius={10}
                    mr={{ xs: 0, sm: 3 }}
                    height="fit-content"
                  >
                    <Avatar
                      alt="icym logo"
                      src="/images/icons/icym_icon.jpg"
                      sx={{ height: 50, width: 50 }}
                    />
                  </Box>
                  <Box>
                    <Typography fontWeight="600">
                      {recordDetails?.postedBy}
                    </Typography>
                    <Typography variant="body2">
                      Last Updated:{" "}
                      {recordDetails?.createdAt?.seconds
                        ? new Date(
                            recordDetails.createdAt.seconds * 1000 +
                              recordDetails.createdAt.nanoseconds / 1000000,
                          ).toLocaleString()
                        : ""}
                    </Typography>
                  </Box>
                </Box>

                {socialImage ? (
                  <Card
                    sx={{
                      overflow: "hidden",
                      borderRadius: { xs: 2, md: 3 },
                    }}
                  >
                    <Box
                      component="img"
                      src={socialImage}
                      alt={recordDetails?.title || "News"}
                      sx={{
                        display: "block",
                        width: "100%",
                        maxHeight: { xs: 240, sm: 380, md: 460 },
                        objectFit: { xs: "contain", sm: "cover" },
                        bgcolor: "#f8fafc",
                      }}
                    />
                  </Card>
                ) : null}
              </Box>
              <Box mt={{ xs: 2.5, md: 3 }} className="news-description">
                <Box
                  dangerouslySetInnerHTML={{
                    __html: recordDetails?.description || "",
                  }}
                />
              </Box>
              {hasGallery ? (
                <Box my={{ xs: 2.5, md: 3 }} sx={{ width: "100%", overflow: "hidden" }}>
                  <ImageGallery
                    items={
                      recordDetails?.gallery?.map?.((item: string) => ({
                        original: item,
                        thumbnail: item,
                      })) || []
                    }
                    showThumbnails={false}
                    showFullscreenButton={false}
                    showPlayButton={false}
                    showNav={recordDetails?.gallery?.length > 1}
                  />
                </Box>
              ) : null}
            </Grid>
          </Grid>
        )}
      </MainLayout>
    </div>
  );
}
